import {
  IsString,
  IsNumber,
  IsOptional,
  IsNotEmpty,
  Min,
} from 'class-validator';
import { BaseDomainEvent } from './post-types';

// Asset usage tracking types
export interface PostUsage {
  postId: string;
  usageType:
    | 'banner'
    | 'attachment'
    | 'featuredVideo'
    | 'content'
    | 'logo'
    | 'siteBanner';
}

export interface AssetUsageInfo {
  usageCount: number;
  usedInPosts: PostUsage[];
  lastUpdated: string;
}

// Consolidated Asset interface (replaces Asset, AssetDto, AssetSummary)
export interface Asset {
  id: string;
  filename: string;
  originalFilename: string;
  url?: string; // Optional - may be undefined when URL generation fails
  s3Key?: string; // Backend field - optional for frontend compatibility
  size: number;
  mimeType: string;
  uploadedAt: string;
  uploadedBy: string;
  siteId: string;
  checksum?: string;
  isExternal?: boolean; // True for external video embeds (any provider)
  externalUrl?: string; // URL for external video embeds (iframe src or direct URL)
  thumbnailS3Key?: string; // S3 key for video thumbnail (backend field)
  thumbnailUrl?: string; // Generated presigned URL for video thumbnail (generated on-demand)
  metadata?: {
    width?: number;
    height?: number;
    duration?: number;
    videoProvider?: string; // For external videos (e.g., 'youtube', 'vimeo', 'custom')
    videoId?: string; // Provider-specific video ID (if applicable)
    [key: string]: unknown;
  };
  usage?: AssetUsageInfo;
}

// Asset event interfaces
export interface AssetCreatedEvent extends BaseDomainEvent {
  asset: Asset;
}

export interface AssetUpdatedEvent extends BaseDomainEvent {
  asset: Asset;
  previousAsset: Asset;
}

export interface AssetDeletedEvent extends BaseDomainEvent {
  assetId: string;
  siteId: string;
  deletedAsset: Asset;
}

// Asset upload DTOs
export class GenerateAssetUploadUrlDto {
  @IsString()
  @IsNotEmpty()
  filename: string;

  @IsString()
  @IsNotEmpty()
  mimeType: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  size?: number;
}

export class CompleteAssetUploadDto {
  @IsString()
  @IsNotEmpty()
  assetId: string;

  @IsOptional()
  @IsString()
  checksum?: string;

  @IsOptional()
  @IsString()
  thumbnailS3Key?: string;

  @IsOptional()
  metadata?: {
    width?: number;
    height?: number;
    duration?: number;
    [key: string]: unknown;
  };
}

export class CheckDuplicateAssetDto {
  @IsString()
  @IsNotEmpty()
  checksum: string;
}

export interface CheckDuplicateAssetResponse {
  isDuplicate: boolean;
  asset?: Asset;
}

export class CreateExternalVideoDto {
  @IsString()
  @IsNotEmpty()
  externalUrl: string;

  @IsOptional()
  @IsString()
  displayName?: string;
}

export class CreateExternalLinkDto {
  @IsString()
  @IsNotEmpty()
  externalUrl: string;

  @IsOptional()
  @IsString()
  displayName?: string;
}

// Asset CRUD DTOs
export class CreateAssetDto {
  @IsString()
  @IsNotEmpty()
  filename: string;

  @IsString()
  @IsNotEmpty()
  originalFilename: string;

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  s3Key: string;

  @IsNumber()
  @Min(0)
  size: number;

  @IsString()
  @IsNotEmpty()
  mimeType: string;

  @IsString()
  @IsNotEmpty()
  uploadedBy: string;

  @IsString()
  @IsNotEmpty()
  siteId: string;

  @IsOptional()
  @IsString()
  checksum?: string;

  @IsOptional()
  metadata?: {
    width?: number;
    height?: number;
    duration?: number;
    [key: string]: unknown;
  };
}

export class UpdateAssetDto {
  @IsOptional()
  @IsString()
  filename?: string;

  @IsOptional()
  @IsString()
  originalFilename?: string;

  @IsOptional()
  @IsString()
  url?: string;

  @IsOptional()
  @IsString()
  s3Key?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  size?: number;

  @IsOptional()
  @IsString()
  mimeType?: string;

  @IsOptional()
  @IsString()
  checksum?: string;

  @IsOptional()
  metadata?: {
    width?: number;
    height?: number;
    duration?: number;
    [key: string]: unknown;
  };
}
